import {
  IFetchAllStagedBidsReportData,
  IFetchAllStagedBidsReportDataRaw,
  IFetchAllUploadedBidsData,
  IFetchAllUploadedBidsDataRaw,
  IStagedBidsData,
  IStagedBidsDataRaw,
} from '../types';

/**
 * Transforms the `brandName` and `categoryName` keys for staged bids into a format
 * that is translation ready for the UI.
 */
export function formatStagedBids(
  response: IStagedBidsDataRaw
): IStagedBidsData {
  const { stagedBids, ...restResp } = response;

  const formattedStagedBids = stagedBids.map((bid) => {
    const {
      brandName,
      brandName_ar: brandNameAr,
      brandName_es: brandNameEs,
      brandName_de: brandNameDe,
      brandName_fr: brandNameFr,
      brandName_he: brandNameHe,
      brandName_nl: brandNameNl,
      brandName_ro: brandNameRo,
      categoryName,
      categoryName_ar: categoryNameAr,
      categoryName_es: categoryNameEs,
      categoryName_de: categoryNameDe,
      categoryName_fr: categoryNameFr,
      categoryName_he: categoryNameHe,
      categoryName_nl: categoryNameNl,
      categoryName_ro: categoryNameRo,
      ...rest
    } = bid;

    return {
      brandName,
      brandNameLocalized: {
        ar: brandNameAr || brandName,
        de: brandNameDe || brandName,
        en: brandName,
        es: brandNameEs || brandName,
        fr: brandNameFr || brandName,
        he: brandNameHe || brandName,
        nl: brandNameNl || brandName,
        ro: brandNameRo || brandName,
      },
      categoryName,
      categoryNameLocalized: {
        ar: categoryNameAr || categoryName,
        de: categoryNameDe || categoryName,
        en: categoryName,
        es: categoryNameEs || categoryName,
        fr: categoryNameFr || categoryName,
        he: categoryNameHe || categoryName,
        nl: categoryNameNl || categoryName,
        ro: categoryNameRo || categoryName,
      },
      ...rest,
    };
  });

  return {
    stagedBids: formattedStagedBids,
    ...restResp,
  };
}

/**
 * Transforms the `brandName` and `categoryName` keys for uploaded bids into a format
 * that is translation ready for the UI.
 */
export function formatUploadedBids(
  response: IFetchAllUploadedBidsDataRaw
): IFetchAllUploadedBidsData {
  const { uploadedFiles, totalCount } = response;

  const formattedUploadedBids = uploadedFiles.map((bid) => {
    const {
      categoryName,
      categoryName_ar: categoryNameAr,
      categoryName_es: categoryNameEs,
      categoryName_de: categoryNameDe,
      categoryName_fr: categoryNameFr,
      categoryName_he: categoryNameHe,
      categoryName_nl: categoryNameNl,
      categoryName_ro: categoryNameRo,
      ...rest
    } = bid;

    return {
      categoryName,
      categoryNameLocalized: {
        ar: categoryNameAr || categoryName,
        de: categoryNameDe || categoryName,
        en: categoryName,
        es: categoryNameEs || categoryName,
        fr: categoryNameFr || categoryName,
        he: categoryNameHe || categoryName,
        nl: categoryNameNl || categoryName,
        ro: categoryNameRo || categoryName,
      },
      ...rest,
    };
  });

  return {
    uploadedFiles: formattedUploadedBids,
    totalCount,
  };
}

/**
 * Transforms the staged bids report response from API format to formatted format.
 * Only includes fields that are actually used in the UI.
 */
export function formatStagedBidsReport(
  response: IFetchAllStagedBidsReportDataRaw
): IFetchAllStagedBidsReportData {
  const { reports, totalCount } = response;

  const formattedReports = reports.map((report) => {
    const {
      _id,
      biddingWindowId,
      biddingWindowName,
      bidValidityConfigId,
      bidValidityConfigurationName,
      categoryName,
      createdAt,
      excelFileS3Path,
    } = report;

    return {
      _id,
      biddingWindowId,
      biddingWindowName,
      bidValidityConfigId,
      bidValidityConfigurationName,
      categoryName,
      createdAt,
      excelFileS3Path,
    };
  });

  return {
    reports: formattedReports,
    totalCount,
  };
}
